import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Target, Heart, BookOpen, Share2, Coffee, Sparkles, Menu, X } from 'lucide-react';
import '../styles/Landing.css';

const Landing = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMobileMenuOpen(prev => !prev);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const dimensions = [
    {
      icon: <CheckCircle size={40} />,
      title: "CNESS Tasks",
      description: "Track and manage your core project tasks with precision and accountability"
    },
    {
      icon: <Heart size={40} />,
      title: "Self-Care",
      description: "Monitor your personal wellness and self-care activities daily"
    },
    {
      icon: <BookOpen size={40} />,
      title: "R&D - Learning",
      description: "Document your research, learning, and skill development journey"
    },
    {
      icon: <Share2 size={40} />,
      title: "Social Media & Content",
      description: "Track content creation and social media engagement efforts"
    },
    {
      icon: <Coffee size={40} />,
      title: "Personal Time",
      description: "Balance work and life by tracking personal and leisure activities"
    },
    {
      icon: <Sparkles size={40} />,
      title: "Inspired Work",
      description: "Capture moments of creativity and inspired contributions"
    }
  ];

  return (
    <div className="landing-page">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay" 
          onClick={closeMobileMenu}
          data-testid="mobile-menu-overlay"
        />
      )}

      {/* Navigation */}
      <nav className="landing-nav" data-testid="landing-nav">
        <div className="nav-container">
          <div className="logo gradient-text" data-testid="logo">CNESS Hive</div>
          
          {/* Mobile Menu Toggle */}
          <button 
            className="mobile-menu-toggle" 
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>

          {/* Navigation Links */}
          <div className={`nav-links ${mobileMenuOpen ? 'open' : ''}`} data-testid="nav-links">
            <a href="#home" onClick={closeMobileMenu} data-testid="nav-home">Home</a>
            <a href="/about" onClick={closeMobileMenu} data-testid="nav-about">About Us</a>
            <a href="#contact" onClick={closeMobileMenu} data-testid="nav-contact">Contact</a>
            <button 
              className="gradient-btn" 
              onClick={() => { navigate('/login'); closeMobileMenu(); }} 
              data-testid="nav-login-btn"
            >
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section fade-in" id="home" data-testid="hero-section">
        <div className="hero-content">
          <h1 className="hero-title" data-testid="hero-title">
            Efficient Project Management Solutions
          </h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            Transform your team's productivity with our innovative project management system for seamless task management.
          </p>
          <button className="hero-cta gradient-btn" onClick={() => navigate('/login')} data-testid="hero-cta-btn">
            Get Started Today
          </button>
        </div>
        <div className="hero-visual">
          <div className="floating-card card-1">
            <Target size={48} className="icon-purple" />
            <span>Goal Tracking</span>
          </div>
          <div className="floating-card card-2">
            <Heart size={48} className="icon-pink" />
            <span>Wellness Focus</span>
          </div>
          <div className="floating-card card-3">
            <Sparkles size={48} className="icon-gradient" />
            <span>Creative Work</span>
          </div>
        </div>
      </section>

      {/* Six Dimensions Section */}
      <section className="dimensions-section" data-testid="dimensions-section">
        <h2 className="section-title">The Six Dimensions of CNESS Hive</h2>
        <p className="section-subtitle">A holistic approach to tracking team productivity and well-being</p>
        <div className="dimensions-grid">
          {dimensions.map((dim, index) => (
            <div className="dimension-card card slide-in" key={index} data-testid={`dimension-card-${index}`} style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="dimension-icon">{dim.icon}</div>
              <h3>{dim.title}</h3>
              <p>{dim.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Vision Section */}
      <section className="vision-section" data-testid="vision-section">
        <div className="vision-content">
          <h2 className="vision-title">CNESS Hive Vision</h2>
          <div className="vision-text">
            <p>
              At CNESS Hive, we envision a workplace where productivity meets well-being. Our platform is built on the belief that sustainable success comes from balancing professional excellence with personal growth.
            </p>
            <p>
              By tracking the six essential dimensions of work and life, we empower teams to understand their time allocation, optimize their workflows, and maintain a healthy work-life balance. CNESS Hive transforms daily logging into actionable insights, helping managers make informed decisions and team members achieve their full potential.
            </p>
            <p>
              Join us in creating a future where every team member thrives, every project succeeds, and every organization grows with purpose and clarity.
            </p>
          </div>
          <div className="vision-features">
            <div className="feature-item">
              <CheckCircle className="feature-icon" />
              <span>Data-Driven Decision Making</span>
            </div>
            <div className="feature-item">
              <CheckCircle className="feature-icon" />
              <span>Holistic Team Management</span>
            </div>
            <div className="feature-item">
              <CheckCircle className="feature-icon" />
              <span>Transparent Progress Tracking</span>
            </div>
            <div className="feature-item">
              <CheckCircle className="feature-icon" />
              <span>Enhanced Team Collaboration</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer" id="contact" data-testid="landing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <h3 className="gradient-text">CNESS Hive</h3>
            <p>Empowering teams to achieve excellence through balanced productivity.</p>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4>Product</h4>
              <a href="#">Features</a>
              <a href="#">Pricing</a>
              <a href="#">Support</a>
            </div>
            <div className="footer-column">
              <h4>Company</h4>
              <a href="/about">About Us</a>
              <a href="#">Careers</a>
              <a href="#">Contact</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 CNESS Hive. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;